import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { slideInFromRightWeighted } from '../../shared/component/Animation';
import { paperTextureSmall } from '../../shared/component/paperTexture';
import { AlertPosition, AlertType, ProjectElementType } from '../../shared/enum/EnumRepository';
import {
  ProjectElementData,
  ProjectElementDataWithWork,
} from '../../shared/dto/EntityRepository';
import { useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { getModalMessage } from '../../shared/constants/modalMessages';
import deleteIcon from '../../asset/icon/delete.png';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import FloorPlanModalIndexing from './FloorPlanModalIndexing';
import FloorPlanModalCreate from './FloorPlanModalCreate';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import {
  useCanShowProjectDetail,
  useCanShowProjectElementList,
} from '../../shared/hooks/useProjectVisibility';

function isWorkProjectElement(pe: ProjectElementData): pe is ProjectElementDataWithWork {
  return pe.projectElementType === ProjectElementType.WORK && pe.work != null;
}

function mergeOrderedPeWorks(
  prev: ProjectElementDataWithWork[],
  source: ProjectElementDataWithWork[]
): ProjectElementDataWithWork[] {
  const sourceKeySet = new Set(source.map((pe) => pe.id));
  const filtered = prev.filter((pe) => sourceKeySet.has(pe.id));
  const filteredKeySet = new Set(filtered.map((pe) => pe.id));
  const added = source.filter((pe) => !filteredKeySet.has(pe.id));
  return [...filtered, ...added];
}

type FloorPlanModalPhase = 'indexing' | 'create';

export interface FloorPlanModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const FloorPlanModal: React.FC<FloorPlanModalProps> = ({ setIsUserInput }) => {
  const { isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const canShowElementList = useCanShowProjectElementList();
  const { projectElementList } = useProjectElementState();

  const workElementsSource = useMemo(
    () => projectElementList.filter(isWorkProjectElement),
    [projectElementList]
  );

  const [phase, setPhase] = useState<FloorPlanModalPhase>('indexing');
  const [orderedWorkElements, setOrderedWorkElements] = useState<ProjectElementDataWithWork[]>(
    []
  );

  useEffect(() => {
    setOrderedWorkElements((prev) => mergeOrderedPeWorks(prev, workElementsSource));
  }, [workElementsSource]);

  useEffect(() => {
    setPhase('indexing');
  }, [project?.id]);

  const baselineIds = useMemo(() => workElementsSource.map((pe) => pe.id), [workElementsSource]);
  const orderedIds = useMemo(() => orderedWorkElements.map((pe) => pe.id), [orderedWorkElements]);
  const indexingDirty = useMemo(
    () =>
      baselineIds.length > 0 &&
      (baselineIds.length !== orderedIds.length ||
        baselineIds.some((id, i) => id !== orderedIds[i])),
    [baselineIds, orderedIds]
  );

  useEffect(() => {
    if (phase === 'indexing') {
      setIsUserInput(indexingDirty);
    }
  }, [phase, indexingDirty, setIsUserInput]);

  useEffect(() => {
    return () => setIsUserInput(false);
  }, [setIsUserInput]);

  const handleCloseModal = () => clearModal(700);

  const handleIndexingNext = () => {
    if (orderedWorkElements.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_WORKS_EMPTY'),
      });
      return;
    }
    setPhase('create');
  };

  if (!canShowProject || !canShowElementList || !project) return null;

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <StationComp>
        <HeaderRow>
          <Title>
            {getModalMessage('FLOOR_PLAN_MODAL_TITLE')} (
            {getModalMessage('WORK_LIST_PDF_SELECTED_COUNT', {
              count: orderedWorkElements.length,
            })}
            )
          </Title>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent={true}
            hoverOpacity={true}
            backgroundImg={deleteIcon}
            onClick={handleCloseModal}
            aria-label="Close"
          />
        </HeaderRow>
        {phase === 'indexing' ? (
          <FloorPlanModalIndexing
            items={orderedWorkElements}
            onReorder={setOrderedWorkElements}
            onNext={handleIndexingNext}
          />
        ) : (
          <FloorPlanModalCreate
            peWorkList={orderedWorkElements}
            onBack={() => setPhase('indexing')}
            setIsUserInput={setIsUserInput}
          />
        )}
      </StationComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean;}>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: clamp(25px, 3vw, 50px);
  width: clamp(440px, 45vw, 600px);
  min-width: 400px;
  height: 92vh;
  max-height: 92vh;
  min-height: 0;
  padding: 20px;
  box-sizing: border-box;
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(100%)' : 'translateX(0)')};
  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const StationComp = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: calc(92vh - 40px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2vh;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Title = styled.div`
  min-width: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

export default FloorPlanModal;
